// Standalone Nav + Footer chrome extracted from _v11_forum.jsx so that
// interior pages can mount the same header/footer used on the homepage.
import React, { useState, useEffect } from 'react';
import { Link, router } from '../_shared.jsx';

// Theme mirrors _v11_forum.jsx — forest-green accent differentiates the
// forum variant from V02 editorial's rust brick.
const theme = {
  bg: '#f8f7f2', fg: '#1a1a1a', muted: '#6b6b6b',
  border: '#e3e0d4', accent: '#15803d', accentFg: '#ffffff',
  inputBg: '#fff', radius: 6,
  font: '"Inter", system-ui, sans-serif',
  bgAlt: '#eef0e8', bgDeep: '#f1f3eb',
};

const mono = {
  fontFamily: '"JetBrains Mono", "SF Mono", ui-monospace, monospace',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const defaultNavLinks = [
  { label: 'Forum', href: '/forum' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Förderfinder', href: '/förderung-finder' },
  { label: 'Glossar', href: '/glossar' },
];

const defaultFooterLinks = [
  { label: 'Forum', href: '/forum' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Förderfinder', href: '/förderung-finder' },
  { label: 'Glossar', href: '/glossar' },
];

export function V11ForumNav({
  brand = 'Waemo',
  brandPrefix = 'Heizung',
  brandSuffix = 'Zukunft',
  navLinks = defaultNavLinks,
  searchPlaceholder = 'Forum durchsuchen — z.B. „Vaillant aroTHERM Jahresarbeitszahl"',
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  if (isMobile) {
    return (
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.border}`,
        background: theme.bg, color: theme.fg, fontFamily: theme.font,
        display: 'flex', flexDirection: 'column', gap: 10, position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link href="/" style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', color: theme.fg, textDecoration: 'none' }}>
            {brandPrefix}<span style={{ color: theme.accent }}>{brandSuffix}</span>
          </Link>
          <button
            aria-label="Menü"
            onClick={() => setIsMenuOpen(o => !o)}
            style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <HamburgerIcon open={isMenuOpen} />
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); router.visit(`/forum?q=${encodeURIComponent(searchQuery)}`); }}
          style={{ display: 'flex' }}
        >
          <input
            name="q"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            style={{
              flex: 1, padding: '8px 12px', border: `1px solid ${theme.border}`,
              background: '#fff', borderRadius: 6, fontSize: 13, color: theme.muted,
            }}
          />
        </form>
        {isMenuOpen && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 12,
            paddingTop: 8, borderTop: `1px solid ${theme.border}`,
          }}>
            {navLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>{l.label}</Link>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{
      padding: '12px 28px', borderBottom: `1px solid ${theme.border}`,
      display: 'flex', alignItems: 'center', gap: 28, fontSize: 13,
      background: theme.bg, color: theme.fg, fontFamily: theme.font,
    }}>
      <Link href="/" style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', color: theme.fg, textDecoration: 'none' }}>
        {brandPrefix}<span style={{ color: theme.accent }}>{brandSuffix}</span>
      </Link>
      <form
        onSubmit={(e) => { e.preventDefault(); router.visit(`/forum?q=${encodeURIComponent(searchQuery)}`); }}
        style={{ flex: 1, display: 'flex' }}
      >
        <input
          name="q"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchPlaceholder}
          style={{
            flex: 1, padding: '8px 12px', border: `1px solid ${theme.border}`,
            background: '#fff', borderRadius: 6, fontSize: 13, color: theme.muted,
          }}
        />
      </form>
      <div style={{ display: 'flex', gap: 14, fontSize: 12, fontWeight: 600, color: theme.muted }}>
        {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
      </div>
    </div>
  );
}

export function V11ForumFooter({
  brand = 'Waemo',
  brandPrefix = 'Heizung',
  brandSuffix = 'Zukunft',
  footerLinks = defaultFooterLinks,
  meta = 'Community · 04/26',
  copyright,
}) {
  const isMobile = useIsMobile();
  const resolvedCopyright = copyright != null ? copyright : `© ${new Date().getFullYear()} ${brand}`;
  return (
    <footer style={{
      background: '#fff',
      borderTop: `1px solid ${theme.border}`,
      padding: isMobile ? '24px 16px' : '40px 56px',
      fontFamily: theme.font,
      color: theme.fg,
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 14 : 28,
        }}>
          <Link href="/" style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', color: theme.fg, textDecoration: 'none' }}>
            {brandPrefix}<span style={{ color: theme.accent }}>{brandSuffix}</span>
          </Link>

          <nav style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 10 : 28,
            fontSize: 14, color: theme.muted, fontWeight: 500,
          }}>
            {footerLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </nav>

          <div style={{ ...mono, fontSize: 11, color: theme.muted, textAlign: isMobile ? 'left' : 'right' }}>
            {meta}
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 10 : 18,
          paddingTop: 16,
          borderTop: `1px solid ${theme.border}`,
        }}>
          <div style={{ ...mono, fontSize: 12, color: theme.muted, letterSpacing: '0.1em' }}>
            {resolvedCopyright}
          </div>
          <nav style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 8 : 22, fontSize: 12, color: theme.muted,
          }} aria-label="Rechtliche Hinweise">
            <Link href="/impressum" style={{ color: theme.muted, textDecoration: 'none' }}>Impressum</Link>
            <Link href="/datenschutz" style={{ color: theme.muted, textDecoration: 'none' }}>Datenschutz</Link>
            <Link href="/agb" style={{ color: theme.muted, textDecoration: 'none' }}>AGB</Link>
            <Link href="/widerruf" style={{ color: theme.muted, textDecoration: 'none' }}>Widerruf</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
